import { Injectable } from '@angular/core';
import { FormFlowFieldTypes } from '@app/Constants';
import { ModuleRecordFeedMappableEntityDto } from '../../Models/FeedConfigurationModels/ModuleRecordFeedMappableEntityDto';
import {IFeedConfigurationDataField} from "@app/Tenant/Applications/Airsweb/Services";

@Injectable({
	providedIn: 'root',
})
export class FeedModuleRecordMapperService {
    findMappableEntityForDataField(
        dataField: Partial<IFeedConfigurationDataField>,
        mappableEntities: ModuleRecordFeedMappableEntityDto[]
    ): ModuleRecordFeedMappableEntityDto | null {
        const dataFieldName = dataField.Name;
		const dataFieldNameSanitised = this.sanitiseString(dataFieldName);
        
		let matchingEntities = mappableEntities.filter((entity) =>
            this.dataFieldCanBeMappedToMappableEntity(dataFieldName, entity, mappableEntities)
        );
        matchingEntities = this.discardEntitiesUnderAmbiguousParents(dataFieldNameSanitised, matchingEntities);

		if (matchingEntities.length === 0) {
			return null;
		}

        const listMatches = matchingEntities.filter(
            (entity) => entity.FieldUniqueId === dataField.DataType
        );
        
        if (listMatches.length > 0){
            return this.findPreferredListMappableEntity(dataFieldNameSanitised, listMatches) || null;
        }
        
        if (matchingEntities.length === 1) {
            return matchingEntities[0];
        }
        return this.findPreferredListMappableEntity(dataFieldNameSanitised, matchingEntities) || matchingEntities[0] || null;
	}

	private findPreferredListMappableEntity(
		dataFieldNameSanitised: string,
		listMatches: ModuleRecordFeedMappableEntityDto[]
	): ModuleRecordFeedMappableEntityDto | null {
		if (!dataFieldNameSanitised) {
			return listMatches[0] || null;
		}

		const preferredModelPropertyNames = [
			`${dataFieldNameSanitised}Text`,
			`${dataFieldNameSanitised}Name`,
			`${dataFieldNameSanitised}AliasText`,
		];

		for (const modelPropertyName of preferredModelPropertyNames) {
			const preferredMatch = listMatches.find(
				(entity) =>
					entity.ModelPropertyName?.toLowerCase() === modelPropertyName.toLowerCase()
			);
			if (preferredMatch) {
				return preferredMatch;
			}
		}

		return listMatches[0] || null;
	}

	private dataFieldCanBeMappedToMappableEntity(
		dataFieldName: string,
		mappableEntity: ModuleRecordFeedMappableEntityDto,
		mappableEntities: ModuleRecordFeedMappableEntityDto[]
	): boolean {
		const dataFieldNameSanitised = this.sanitiseString(dataFieldName);
		if (
			this.dataFieldCanBeMappedFromMappableEntityModelPropertyName(
				dataFieldNameSanitised,
				mappableEntity.ModelPropertyName
			)
		) {
			return true;
		}

		if (this.dataFieldNamesParentAndPropertyOfMappableEntity(dataFieldNameSanitised, mappableEntity)) {
			return true;
		}

		return this.dataFieldCanBeMappedFromMappableEntityLabel(
			dataFieldNameSanitised,
			mappableEntity,
			mappableEntities
		);
	}

	// A property such as SystemId can sit on several multis, so only a data field naming the parent
	// as well - "Witness System Id" - says which of them is meant.
	private dataFieldNamesParentAndPropertyOfMappableEntity(
		dataFieldNameSanitised: string,
		mappableEntity: ModuleRecordFeedMappableEntityDto
	): boolean {
		if (!dataFieldNameSanitised) {
			return false;
		}

		return this.getParentNames(mappableEntity).some((parentName) =>
			this.getPropertyNames(mappableEntity).some(
				(propertyName) =>
					`${parentName}${propertyName}`.toLowerCase() ===
					dataFieldNameSanitised.toLowerCase()
			)
		);
	}

	private discardEntitiesUnderAmbiguousParents(
		dataFieldNameSanitised: string,
		matchingEntities: ModuleRecordFeedMappableEntityDto[]
	): ModuleRecordFeedMappableEntityDto[] {
		return matchingEntities.filter((entity) => {
			const sharesPropertyWithAnotherParent = matchingEntities.some(
				(otherEntity) =>
					otherEntity.ModelPropertyName === entity.ModelPropertyName &&
					this.getParentPath(otherEntity) !== this.getParentPath(entity)
			);

			if (!sharesPropertyWithAnotherParent) {
				return true;
			}

			return this.dataFieldNamesParentAndPropertyOfMappableEntity(
				dataFieldNameSanitised,
				entity
			);
		});
	}

	// Path is [model, ...parents, ModelPropertyName] and PathLabel is
	// "<parent labels>/<field label>/<ModelPropertyName> (<field type>)", so a parent can be named
	// either by its model property or by its label.
	private getParentNames(mappableEntity: ModuleRecordFeedMappableEntityDto): string[] {
		const pathParents = (mappableEntity.Path ?? []).slice(1, -1);
		const pathLabelParents = (mappableEntity.PathLabel ?? '').split('/').slice(0, -2);

		return [...pathParents, ...pathLabelParents]
			.map((parentName) => this.sanitiseString(parentName))
			.filter((parentName) => !!parentName);
	}

	private getPropertyNames(mappableEntity: ModuleRecordFeedMappableEntityDto): string[] {
		return [mappableEntity.ModelPropertyName, mappableEntity.Label]
			.map((propertyName) => this.sanitiseString(propertyName))
			.filter((propertyName) => !!propertyName);
	}

	private getParentPath(mappableEntity: ModuleRecordFeedMappableEntityDto): string {
		return (mappableEntity.Path ?? []).slice(0, -1).join('.');
	}

	private sanitiseString(stringToSanitise: string): string {
		return !stringToSanitise ? stringToSanitise : stringToSanitise.replace(/[\s?]/g, '');
	}

	private dataFieldCanBeMappedFromMappableEntityModelPropertyName(
		dataFieldNameSanitised: string,
		mappableEntityModelPropertyName: string
	): boolean {
		const possibleMappableEntityModelPropertyNames = [
			dataFieldNameSanitised,
			`${dataFieldNameSanitised}Text`,
			`${dataFieldNameSanitised}AliasText`,
			`${dataFieldNameSanitised}Name`,
			`${dataFieldNameSanitised}Date`,
		];

		return (
			possibleMappableEntityModelPropertyNames.filter((str) =>
				str.toLowerCase().startsWith(mappableEntityModelPropertyName.toLowerCase())
			).length > 0
		);
	}

	private dataFieldCanBeMappedFromMappableEntityLabel(
		dataFieldNameSanitised: string,
		mappableEntity: ModuleRecordFeedMappableEntityDto,
		mappableEntities: ModuleRecordFeedMappableEntityDto[]
	): boolean {
		if (!dataFieldNameSanitised) {
			return false;
		}

		const mappableEntityLabel = this.sanitiseString(mappableEntity.Label);
		if (!mappableEntityLabel) {
			return false;
		}

		if (mappableEntityLabel.toLowerCase() !== dataFieldNameSanitised.toLowerCase()) {
			return false;
		}

		if (this.mappableEntityIsAListAndCanBeMappedToDataField(mappableEntity)) {
			return true;
		}

		if (this.mappableEntityIsADateTimePickerAndCanBeMappedToDataField(mappableEntity)) {
			return true;
		}

		return this.labelAppearsOnlyOnceInMappableEntities(dataFieldNameSanitised, mappableEntities);
	}

	private mappableEntityIsADateTimePickerAndCanBeMappedToDataField(
		mappableEntity: ModuleRecordFeedMappableEntityDto
	): boolean {
		return (
			mappableEntity.FieldUniqueId === FormFlowFieldTypes.DateTimePicker &&
			(mappableEntity.ModelPropertyName.endsWith('Date') ||
				mappableEntity.ModelPropertyName.startsWith('Date'))
		);
	}

	private mappableEntityIsAListAndCanBeMappedToDataField(
		mappableEntity: ModuleRecordFeedMappableEntityDto
	): boolean {
		return (
			mappableEntity.FieldUniqueId === FormFlowFieldTypes.List &&
			(mappableEntity.ModelPropertyName.endsWith('Text') ||
				mappableEntity.ModelPropertyName.endsWith('Name'))
		);
	}

	private labelAppearsOnlyOnceInMappableEntities(
		dataFieldNameSanitised: string,
		mappableEntities: ModuleRecordFeedMappableEntityDto[]
	): boolean {
		return (
			mappableEntities.filter((entity) => {
				const sanitisedEntity = this.sanitiseString(entity.Label);
				return (
					!!dataFieldNameSanitised &&
					!!sanitisedEntity &&
					sanitisedEntity.toLowerCase() === dataFieldNameSanitised.toLowerCase()
				);
			}).length === 1
		);
	}
}
